<template>
  <div @click="emit('onPlanSelected', product.id)" class="my-2 lg:my-0 transition duration-250 relative p-8 bg-white rounded-2xl shadow-sm flex flex-col lg:w-1/4" :class="{ 'border-black border': isSelectedProduct, 'border border-gray-200': !isSelectedProduct, 'cursor-pointer': canSelectProduct }">
    <div class="flex-1">
      <h3 class="text-xl font-semibold text-gray-900">{{ product.name }}</h3>
      <p v-if="product.download === 250" class="absolute top-0 py-1.5 px-4 bg-brand-primary rounded-full text-xs font-semibold tracking-wide text-brand-button-text transform -translate-y-1/2">Most Popular!</p>

      <div class="mt-0 lg:mt-4">
        <div v-if="!product.id && !brandService.removeDefaultPlansFrom()" class="mb-2 uppercase font-extrabold tracking-wide text-xs">From</div>
        <div class="flex items-end text-gray-900">
          <div class="leading-7">
            <span :class="['tracking-tight font-extrabold', product.displayDiscount ? 'text-2xl text-gray-300 text-decoration-line: line-through' : 'text-4xl font-extrabold']">${{ product.price }}</span>
            <p v-if="product.displayDiscount" class="mb-1 tracking-tight font-extrabold text-4xl">${{ product.discountedPrice }}</p>
          </div>
          <span class="ml-1 text-xl font-semibold">/Month</span>
        </div>
      </div>

      <div><span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-brand-primary text-brand-button-text">Unlimited Data!</span></div>
      <p v-if="product.typicalEveningSpeed" class="my-4 lg:my-6 text-gray-500"><span class="font-extrabold text-gray-500">{{ product.typicalEveningSpeed }} Mbps</span> typical evening speed</p>
      <p v-if="product.download ? product.download > 100 : false" class="my-4 lg:my-6 text-gray-500">Average sampled evening speed not currently available</p>
      <!-- Feature list -->
      <ul role="list" class="mt-6 mb-6 space-y-6 hidden lg:block">
        <li class="flex">
          <CheckIcon class="flex-shrink-0 w-6 h-6 text-brand-secondary-accent" aria-hidden="true" />
          <span class="ml-3 text-gray-500">{{ product.description }}</span>
        </li>

        <li v-for="standardFeature in standardFeatures" :key="standardFeature" class="flex">
          <CheckIcon class="flex-shrink-0 w-6 h-6 text-brand-secondary-accent" aria-hidden="true" />
          <span class="ml-3 text-gray-500">{{ standardFeature }}</span>
        </li>
      </ul>
      <!-- Links -->
      <a v-for="standardLink in standardLinks" :key="standardLink.name" :href="standardLink.link" target="_blank" class="block href text-blue-500 hover:text-blue-800 hover:underline transition duration-250 my-3">
        {{ standardLink.name }}
      </a>
    </div>
    <button v-if="canSelectProduct" class="transition duration-250" :class="[mostPopular, 'mt-0 lg:mt-8 block w-full py-3 px-6 border border-transparent rounded-md text-center font-medium text-brand-button-text', isSelectedProduct ? 'bg-brand-button' : 'bg-brand-primary-light']">{{ isSelectedProduct ? 'Selected' : 'Select Plan'}}</button>
  </div>
</template>

<script setup lang='ts'>
import { CheckIcon } from '@heroicons/vue/outline';
import { defineProps, defineEmits, computed, ref } from 'vue';
import { BrandServiceInterface, PlanSelectorProductInformation } from '@/services/brand/brandServiceInterface';
import { injectStrict } from '@/plugins/vueServiceContainerPlugin';

const brandService = injectStrict<BrandServiceInterface>('BrandServiceInterface');

type Product = {
  id: string,
  name: string,
  description: string,
  download: number | null,
  upload: number | null,
  typicalEveningSpeed: number | null,
  price: number,
  discountedPrice?: number,
  discounted?: boolean,
  displayDiscount?: boolean
}

const props = defineProps<{
  canSelectProduct: boolean,
  isSelectedProduct: boolean,
  product: Product,
  planSelectorProductInformation: PlanSelectorProductInformation
}>();

const emit = defineEmits<{(event: 'onPlanSelected', id: string): void}>();

const mostPopular = computed(() => {
  return props.product.download === 250
    ? 'text-white hover:bg-brand-primary'
    : 'text-brand-secondary hover:bg-brand-primary';
});

const standardFeatures = ref(props.planSelectorProductInformation.standardFeatures).value;
const standardLinks = ref(props.planSelectorProductInformation.standardLinks).value;

</script>
